import { SecurityDomainError } from '@/services/business/security/problems';
import type {
  AccessPolicyResource,
  IpRulesResource,
  SecurityActor,
  SecurityEvent,
  SecuritySession,
} from '@/services/business/security/types';
import { createRandomId } from '@/utils/randomId';
import { loginCaptchaConfig } from '../store/loginCaptchaConfig';
import { defaultSecurityPolicy, seedSecurity } from './security-seed';
import {
  record,
  validateIpRule,
  validateLoginPolicy,
  validateNote,
  validateProtectionPolicy,
  validateResolution,
} from './security-validation';

let state = seedSecurity();
let versions = { events: 1, sessions: 1, accessPolicy: 1, ipRules: 1 };
const initialCaptcha = structuredClone(loginCaptchaConfig);
let policyDraft = structuredClone(defaultSecurityPolicy);
const timers = new Set<ReturnType<typeof setTimeout>>();

export function clearSecurityTimers() {
  for (const timer of timers) {
    clearTimeout(timer);
  }
  timers.clear();
}
function schedule(at: string, transition: () => void) {
  const timer = setTimeout(
    () => {
      timers.delete(timer);
      if (Date.parse(at) > Date.now()) {
        schedule(at, transition);
      } else {
        transition();
      }
    },
    Math.min(2 ** 31 - 1, Math.max(0, Date.parse(at) - Date.now())),
  );
  timers.add(timer);
  const handle: unknown = timer;
  if (
    handle &&
    typeof handle === 'object' &&
    'unref' in handle &&
    typeof handle.unref === 'function'
  ) {
    handle.unref();
  }
}
function endRestriction(event: SecurityEvent, status: 'expired' | 'released') {
  if (event.restriction?.status !== 'active') {
    return;
  }
  event.restriction.status = status;
  for (const rule of state.deniedIps) {
    if (rule.source === 'automatic' && rule.eventId === event.id) {
      rule.enabled = false;
    }
  }
  versions.events += 1;
  versions.ipRules += 1;
}
function scheduleExpirations() {
  clearSecurityTimers();
  for (const event of state.events) {
    if (event.restriction?.status === 'active') {
      schedule(event.restriction.expiresAt, () => endRestriction(event, 'expired'));
    }
  }
  for (const rule of [...state.allowedIps, ...state.deniedIps]) {
    if (rule.enabled && rule.expiresAt && rule.source === 'manual') {
      schedule(rule.expiresAt, () => {
        if (rule.enabled) {
          rule.enabled = false;
          versions.ipRules += 1;
        }
      });
    }
  }
}
export function resetSecurityCenter() {
  clearSecurityTimers();
  state = seedSecurity();
  versions = { events: 1, sessions: 1, accessPolicy: 1, ipRules: 1 };
  policyDraft = structuredClone(defaultSecurityPolicy);
  Object.assign(loginCaptchaConfig, structuredClone(initialCaptcha));
  scheduleExpirations();
}
function requireVersion(expected: unknown, resource: keyof typeof versions) {
  if (!Number.isInteger(expected) || Number(expected) < 1) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  if (expected !== versions[resource]) {
    throw new SecurityDomainError('stateChanged', 409, {
      resource,
      expectedVersion: expected,
      currentVersion: versions[resource],
    });
  }
}
function command(input: unknown): Record<string, unknown> {
  if (!record(input)) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  return input;
}
export function accessPolicyResource(): AccessPolicyResource {
  return structuredClone({
    version: versions.accessPolicy,
    login: { ...loginCaptchaConfig, security: { policyDraft } },
    protection: { autoProtection: state.autoProtection },
    smsProtection: state.smsProtection,
  });
}
export function ipRulesResource(): IpRulesResource {
  return structuredClone({
    version: versions.ipRules,
    allowed: state.allowedIps,
    denied: state.deniedIps,
  });
}
type Query = Record<string, string | string[] | undefined>;
function text(value: string | string[] | undefined) {
  return (Array.isArray(value) ? value[0] : value) ?? '';
}
function page<T>(items: T[], query: Query, version: number) {
  const pageNumber = Math.max(1, Math.trunc(Number(text(query.page)) || 1));
  const pageSize = Math.min(
    100,
    Math.max(1, Math.trunc(Number(text(query.page_size)) || 10)),
  );
  return structuredClone({
    items: items.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    total: items.length,
    page: pageNumber,
    page_size: pageSize,
    version,
  });
}
function matches(row: SecurityEvent | SecuritySession, query: Query) {
  const search = text(query.account).trim().toLowerCase();
  const date = Date.parse('lastActiveAt' in row ? row.lastActiveAt : row.createdAt);
  return (
    (!search || `${row.account} ${row.id} ${row.ip}`.toLowerCase().includes(search)) &&
    (!text(query.status) || row.status === text(query.status)) &&
    (!text(query.date_from) || date >= Date.parse(text(query.date_from))) &&
    (!text(query.date_to) || date <= Date.parse(text(query.date_to)))
  );
}
export function securityEvents(query: Query) {
  return page(
    state.events.filter(
      (row) =>
        matches(row, query) &&
        (!text(query.type) || row.type === text(query.type)) &&
        (!text(query.severity) || row.severity === text(query.severity)) &&
        (!text(query.resolution) || row.resolution === text(query.resolution)),
    ),
    query,
    versions.events,
  );
}
export function securitySessions(query: Query) {
  return page(
    state.sessions.filter((row) => matches(row, query)),
    query,
    versions.sessions,
  );
}
export function securityIpRules(query: Query) {
  const list = text(query.list);
  const rows =
    list === 'allowed'
      ? state.allowedIps
      : list === 'denied'
        ? state.deniedIps
        : [...state.allowedIps, ...state.deniedIps];
  const search = text(query.address).trim().toLowerCase();
  return page(
    rows.filter(
      (row) =>
        (!search || `${row.address} ${row.note}`.toLowerCase().includes(search)) &&
        (!text(query.enabled) || String(row.enabled) === text(query.enabled)) &&
        (!text(query.source) || row.source === text(query.source)),
    ),
    query,
    versions.ipRules,
  );
}
export function updateLoginPolicy(input: unknown) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'accessPolicy');
  const values = validateLoginPolicy(body.values);
  Object.assign(loginCaptchaConfig, {
    maxFailures: values.maxFailures,
    lockMinutes: values.lockMinutes,
    humanVerification: values.humanVerification,
  });
  policyDraft = values.security.policyDraft;
  versions.accessPolicy += 1;
  return accessPolicyResource();
}
export function updateProtectionPolicy(input: unknown) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'accessPolicy');
  const values = validateProtectionPolicy(body.values);
  state.autoProtection = values.autoProtection;
  state.smsProtection = values.smsProtection;
  versions.accessPolicy += 1;
  return accessPolicyResource();
}
function ipList(value: unknown) {
  if (value === 'allowed') {
    return state.allowedIps;
  }
  if (value === 'denied') {
    return state.deniedIps;
  }
  throw new SecurityDomainError('invalidPolicy', 422);
}
function manualRule(list: ReturnType<typeof ipList>, id: unknown) {
  const row = list.find((item) => item.id === id);
  if (!row) {
    throw new SecurityDomainError('notFound', 404);
  }
  if (row.source !== 'manual') {
    throw new SecurityDomainError('permissionDenied', 403);
  }
  return row;
}
function uniqueIp(address: string, id?: string) {
  if (
    [...state.allowedIps, ...state.deniedIps].some(
      (row) => row.id !== id && row.address === address,
    )
  ) {
    throw new SecurityDomainError('duplicateIp', 422);
  }
}
export function createIpRule(input: unknown) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'ipRules');
  const list = ipList(body.list);
  if (list.length >= 100) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  const values = validateIpRule(body.values, Date.now());
  uniqueIp(values.address);
  list.push({ ...values, id: createRandomId(), source: 'manual' });
  versions.ipRules += 1;
  scheduleExpirations();
  return ipRulesResource();
}
export function updateIpRule(input: unknown) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'ipRules');
  const row = manualRule(ipList(body.list), body.id);
  const values = validateIpRule(body.values, Date.now());
  uniqueIp(values.address, row.id);
  Object.assign(row, values);
  versions.ipRules += 1;
  scheduleExpirations();
  return ipRulesResource();
}
export function deleteIpRule(input: unknown) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'ipRules');
  const list = ipList(body.list);
  const row = manualRule(list, body.id);
  list.splice(list.indexOf(row), 1);
  versions.ipRules += 1;
  scheduleExpirations();
  return ipRulesResource();
}
function eventById(id: string) {
  const row = state.events.find((item) => item.id === id);
  if (!row) {
    throw new SecurityDomainError('notFound', 404);
  }
  return row;
}
export function processSecurityEvent(id: string, input: unknown, actor: SecurityActor) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'events');
  const row = eventById(id);
  const note = validateNote(body.note);
  const resolution = validateResolution(body.resolution);
  if (row.status !== 'open') {
    throw new SecurityDomainError('stateChanged', 409);
  }
  const now = new Date().toISOString();
  Object.assign(row, {
    status: 'resolved',
    resolution,
    processedAt: now,
    processedBy: actor,
    note,
  });
  row.handlingRecords.push({ resolution, at: now, actor, note });
  versions.events += 1;
}
export function releaseSecurityRestriction(
  id: string,
  input: unknown,
  actor: SecurityActor,
) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'events');
  const row = eventById(id);
  const note = validateNote(body.note);
  if (row.restriction?.status !== 'active') {
    throw new SecurityDomainError('stateChanged', 409);
  }
  Object.assign(row.restriction, {
    releasedAt: new Date().toISOString(),
    releasedBy: actor,
    releaseNote: note,
  });
  endRestriction(row, 'released');
  scheduleExpirations();
}
export function terminateSecuritySession(id: string, input: unknown) {
  const body = command(input);
  requireVersion(body.expectedVersion, 'sessions');
  const row = state.sessions.find((item) => item.id === id);
  if (!row) {
    throw new SecurityDomainError('notFound', 404);
  }
  const note = validateNote(body.note);
  if (row.current) {
    throw new SecurityDomainError('currentProtected', 409);
  }
  if (row.status !== 'active') {
    throw new SecurityDomainError('stateChanged', 409);
  }
  Object.assign(row, { status: 'revoked', note, endedAt: new Date().toISOString() });
  versions.sessions += 1;
}
scheduleExpirations();
